import { AlertCircle, Database, TrendingUp } from 'lucide-react';
import type { ExploreAccess } from '../../types/explore';

/**
 * The opt-in gate.
 *
 * This is the conversion surface for the whole flywheel, so it states the deal plainly rather than
 * dressing it up: your captures go into a shared pool, and in exchange you can read everyone
 * else's. An agency that feels tricked into contributing will turn it off and tell others.
 *
 * It is also where the three refusal reasons become three different screens. "You have not joined",
 * "you are reading but not contributing" and "you have used this month's reads" need different
 * words and different buttons; collapsing them into one "access denied" would leave someone
 * clicking a button that cannot help them.
 */

function QuotaExceeded({ access }: { access: ExploreAccess }) {
  return (
    <div className="rounded-lg border border-glass bg-glass-1 p-6 text-center">
      <AlertCircle className="mx-auto h-8 w-8 text-[rgb(var(--warn))]" aria-hidden="true" />
      <h2 className="mt-3 text-lg font-semibold text-glass-text">You have used this month&apos;s reads</h2>
      <p className="mx-auto mt-2 max-w-md text-sm text-glass-text-2">
        {access.used.toLocaleString()} of {access.quota?.toLocaleString() ?? '—'} reads used in the
        last 30 days. The allowance grows with what you contribute — every creator your team
        captures with the browser extension adds ten reads.
      </p>
      <p className="mt-3 text-xs text-glass-text-3">
        You have contributed {access.contributedCount.toLocaleString()} captures so far.
      </p>
    </div>
  );
}

export default function PoolOptInGate({
  access,
  onJoin,
  joining,
  error,
}: {
  access: ExploreAccess;
  onJoin: () => void;
  joining: boolean;
  error?: string | null;
}) {
  if (access.reason === 'QUOTA_EXCEEDED') return <QuotaExceeded access={access} />;

  return (
    <div className="rounded-lg border border-glass bg-glass-1 p-8">
      <div className="mx-auto max-w-2xl text-center">
        <Database className="mx-auto h-10 w-10 text-accent2" aria-hidden="true" />
        <h2 className="mt-4 text-xl font-semibold text-glass-text">
          Search creators your team has never met
        </h2>
        <p className="mt-3 text-sm text-glass-text-2">
          Explore searches a shared pool built by every agency on kol.my. Join it and your team can
          search creators discovered by everyone else — and the pool gets better for you every time
          another agency uses it.
        </p>

        <div className="mt-6 grid gap-4 text-left sm:grid-cols-2">
          <div className="rounded-md border border-glass p-4">
            <h3 className="flex items-center gap-2 text-sm font-medium text-glass-text">
              <TrendingUp className="h-4 w-4 text-[rgb(var(--pos))]" aria-hidden="true" />
              What you get
            </h3>
            <ul className="mt-2 space-y-1 text-sm text-glass-text-2">
              <li>Search by platform, followers, engagement and growth</li>
              <li>Add any creator to your roster in one click</li>
              <li>Unlimited reads for your first 30 days</li>
            </ul>
          </div>
          <div className="rounded-md border border-glass p-4">
            <h3 className="text-sm font-medium text-glass-text">What you share</h3>
            <ul className="mt-2 space-y-1 text-sm text-glass-text-2">
              <li>Public profile metrics your team captures: handle, followers, engagement</li>
              <li>
                <strong className="text-glass-text">Never</strong> your notes, rates, contacts or
                campaign history
              </li>
              <li>
                <strong className="text-glass-text">Never</strong> creator email addresses, even
                public ones
              </li>
            </ul>
          </div>
        </div>

        <p className="mt-5 text-xs text-glass-text-3">
          Only publicly available profile information is shared. Creators can ask to be removed at
          any time, and a removed creator cannot be re-added. You can leave the pool whenever you
          like — captures you have already contributed stay, because other agencies are relying on
          them.
        </p>

        {error && (
          <p className="mt-4 text-sm text-[rgb(var(--danger))]" role="alert">
            {error}
          </p>
        )}

        <button
          type="button"
          onClick={onJoin}
          disabled={joining}
          className="mt-6 rounded-md bg-accent2 px-5 py-2.5 text-sm font-medium text-white disabled:opacity-60"
        >
          {joining ? 'Joining…' : 'Join the shared pool'}
        </button>
        <p className="mt-2 text-xs text-glass-text-3">
          By joining you agree to share the public profile metrics your team captures.
        </p>
      </div>
    </div>
  );
}
